import React, { Component } from 'react';
import { connect } from 'react-redux';
import './PaymentSuccess.css';
import axios from 'axios';
import API from '../../config/API';

class PaymentSuccess extends Component {
  constructor(props) {
    super(props);
    this.state = {
      bookingId: null,
      transactionId: null,
      booking: null,
      loading: true,
      error: false
    };
  }

  componentDidMount() {
    // Parse URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const bookingId = urlParams.get('bid');
    const transactionId = urlParams.get('tid');

    this.setState({ bookingId, transactionId });

    // Fetch booking details
    if (bookingId && transactionId) {
      this.fetchBookingDetails(bookingId, transactionId);
    } else {
      this.setState({ loading: false, error: true });
    }
  }

  fetchBookingDetails(bookingId, transactionId) {
    // Use public endpoint - no authentication required
    axios.get(API.PAYMENT_SUCCESS_BOOKING, {
      params: {
        bookingId: bookingId,
        transactionId: transactionId
      }
    })
      .then(response => {
        if (response.data && response.data.statusCode === 1) {
          this.setState({
            booking: response.data.responseData,
            loading: false
          });
        } else {
          this.setState({ loading: false, error: true });
        }
      })
      .catch(error => {
        console.error('Error fetching booking:', error);
        this.setState({ loading: false, error: true });
      });
  }

  formatCurrency(amount) {
    return `£${parseFloat(amount || 0).toFixed(2)}`;
  }

  formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return date.toLocaleDateString('en-GB', options);
  }

  render() {
    const { loading, error, booking } = this.state;

    if (loading) {
      return (
        <div className="payment-success-container">
          <div className="payment-success-card loading">
            <div className="spinner"></div>
            <p>Loading payment details...</p>
          </div>
        </div>
      );
    }

    if (error || !booking) {
      return (
        <div className="payment-success-container">
          <div className="payment-success-card error">
            <div className="error-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
            </div>
            <h1>Payment Not Found</h1>
            <p>We couldn't find the payment details. Please contact support if you believe this is an error.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="payment-success-container">
        <div className="payment-success-card">
          {/* Success Animation */}
          <div className="success-animation">
            <div className="checkmark-circle">
              <svg className="checkmark" viewBox="0 0 52 52">
                <circle className="checkmark-circle-path" cx="26" cy="26" r="25" fill="none"/>
                <path className="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
              </svg>
            </div>
          </div>

          {/* Success Message */}
          <div className="success-content">
            <h1 className="success-title">Payment Successful!</h1>
            <p className="success-subtitle">Thank you for your payment</p>
          </div>

          {/* Booking Details Card */}
          <div className="booking-details-card">
            <div className="detail-row">
              <span className="detail-label">Booking Reference</span>
              <span className="detail-value">#{booking.id}</span>
            </div>
            
            <div className="detail-row">
              <span className="detail-label">Amount Paid</span>
              <span className="detail-value amount">{this.formatCurrency(booking.totalAmount)}</span>
            </div>

            {booking.bookingDate && (
              <div className="detail-row">
                <span className="detail-label">Appointment Date</span>
                <span className="detail-value">{this.formatDate(booking.bookingDate)}</span>
              </div>
            )}

            {booking.salonName && (
              <div className="detail-row">
                <span className="detail-label">Salon</span>
                <span className="detail-value">{booking.salonName}</span>
              </div>
            )}

            {booking.hairdresserName && (
              <div className="detail-row">
                <span className="detail-label">Professional</span>
                <span className="detail-value">{booking.hairdresserName}</span>
              </div>
            )}
          </div>

          {/* Services List */}
          {booking.services && booking.services.length > 0 && (
            <div className="services-card">
              <h3 className="services-title">Services Booked</h3>
              <div className="services-list">
                {booking.services.map((service, index) => (
                  <div key={index} className="service-item">
                    <span className="service-name">{service.serviceName || service.name}</span>
                    <span className="service-price">{this.formatCurrency(service.price)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Products List */}
          {booking.products && booking.products.length > 0 && (
            <div className="services-card">
              <h3 className="services-title">Products Purchased</h3>
              <div className="services-list">
                {booking.products.map((product, index) => (
                  <div key={index} className="service-item">
                    <span className="service-name">{product.productName}</span>
                    <span className="service-price">{this.formatCurrency(product.price)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Vouchers List */}
          {booking.vouchers && booking.vouchers.length > 0 && (
            <div className="services-card">
              <h3 className="services-title">Vouchers Purchased</h3>
              <div className="services-list">
                {booking.vouchers.map((voucher, index) => (
                  <div key={index} className="service-item">
                    <span className="service-name">
                      {voucher.type === 'cash' ? 'Cash Voucher' : 'Time Voucher'} - {voucher.code}
                    </span>
                    <span className="service-price">{this.formatCurrency(voucher.price)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Confirmation Message */}
          <div className="confirmation-message">
            <div className="message-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
            </div>
            <p>Need an email receipt? Please contact {booking.hairdresserName || 'your professional'} to request one</p>
          </div>

        </div>

        {/* Decorative Elements */}
        <div className="confetti-container">
          {[...Array(50)].map((_, i) => (
            <div 
              key={i} 
              className="confetti"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                backgroundColor: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8'][Math.floor(Math.random() * 5)]
              }}
            />
          ))}
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {};
}

const connectedPaymentSuccess = connect(mapStateToProps)(PaymentSuccess);
export default connectedPaymentSuccess;